import React from "react";
import { Chip, SvgIconProps } from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import PowerOffIcon from "@mui/icons-material/PowerOff";
import LoginIcon from "@mui/icons-material/Login";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import KeyOffIcon from "@mui/icons-material/KeyOff";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import SyncIcon from "@mui/icons-material/Sync";
import VerifiedIcon from "@mui/icons-material/Verified";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";
import { TimelineEvent } from "@/domain/troubleshooting.interface";

/**
 * COYN-1507 §6: notable event types get their own icon so crashes and
 * sign-in problems stand out on the timeline. `alert` types are drawn with a
 * square marker and a tag in the event list.
 */
export interface EventTypeMeta {
  Icon: React.ComponentType<SvgIconProps>;
  /** Key under `eventTypes.` in the troubleshooting namespace. */
  labelKey: string;
  alert: boolean;
}

const BY_TYPE: Record<string, EventTypeMeta> = {
  JS_CRASH: { Icon: BugReportIcon, labelKey: "crash", alert: true },
  ABRUPT_TERMINATION: {
    Icon: PowerOffIcon,
    labelKey: "abruptTermination",
    alert: true,
  },
  LOGIN_REQUIRED: { Icon: LoginIcon, labelKey: "loginRequired", alert: true },
  SESSION_LOST_UNEXPECTEDLY: {
    Icon: LinkOffIcon,
    labelKey: "sessionLost",
    alert: true,
  },
  SERVER_REFRESH_REJECTED: {
    Icon: KeyOffIcon,
    labelKey: "refreshRejected",
    alert: true,
  },
  // Contract §7: derived from a NOT_GENERATED / ERROR decision so the
  // Notifications lane shows sent vs skipped vs not generated vs failed.
  NOTIFICATION_NOT_GENERATED: {
    Icon: NotificationsOffIcon,
    labelKey: "notificationNotGenerated",
    alert: false,
  },
  // Contract §11.2: geofence SDK configuration on the device.
  SDK_CONFIG_APPLIED: {
    Icon: SettingsSuggestIcon,
    labelKey: "sdkConfigApplied",
    alert: false,
  },
  SDK_START_BLOCKED: {
    Icon: WarningAmberIcon,
    labelKey: "sdkStartBlocked",
    alert: true,
  },
  EVENT_WITHOUT_HTTP_CONFIG: {
    Icon: WarningAmberIcon,
    labelKey: "eventWithoutHttpConfig",
    alert: true,
  },
  ADDRESS_SYNC_RESULT: {
    Icon: SyncIcon,
    labelKey: "addressSyncResult",
    alert: false,
  },
  SERVER_ADDRESS_SYNC_CONFIRMED: {
    Icon: VerifiedIcon,
    labelKey: "addressSyncConfirmed",
    alert: false,
  },
};

const PENDING_TAP: EventTypeMeta = {
  Icon: TouchAppIcon,
  labelKey: "pendingTap",
  alert: false,
};

/** Termination while backgrounded is normal on iOS (contract §6.2). */
const BENIGN_TERMINATION = "TERMINATED_IN_BACKGROUND";

export const getEventTypeMeta = (
  event: Pick<TimelineEvent, "type" | "reasonCode">,
): EventTypeMeta | undefined => {
  const type = event.type || "";
  if (
    type === "ABRUPT_TERMINATION" &&
    event.reasonCode === BENIGN_TERMINATION
  ) {
    return { ...BY_TYPE.ABRUPT_TERMINATION, alert: false };
  }
  if (BY_TYPE[type]) return BY_TYPE[type];
  if (type.startsWith("PENDING_TAP_")) return PENDING_TAP;
  // SESSION_LOST_UNEXPECTEDLY may also arrive as the reasonCode of
  // SESSION_MISSING_ON_LAUNCH (contract §6.2).
  if (event.reasonCode === "SESSION_LOST_UNEXPECTEDLY") {
    return BY_TYPE.SESSION_LOST_UNEXPECTEDLY;
  }
  return undefined;
};

export const EventTypeTag: React.FC<{
  event: Pick<TimelineEvent, "type" | "reasonCode">;
}> = ({ event }) => {
  const { t } = useTranslation("troubleshooting");
  const tt = t as (key: string) => string;
  const meta = getEventTypeMeta(event);
  if (!meta) return null;
  return (
    <Chip
      size="small"
      variant={meta.alert ? "filled" : "outlined"}
      color={meta.alert ? "error" : "default"}
      icon={<meta.Icon aria-hidden />}
      label={tt(`eventTypes.${meta.labelKey}`)}
      data-testid="event-type-tag"
      data-alert={meta.alert || undefined}
    />
  );
};
